import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Clock, Download, ChevronRight, ArrowRight, BookOpen } from 'lucide-react';
import Navbar from '../components/Navbar.jsx';
import Footer from '../components/Footer.jsx';
import Seo from '../components/Seo.jsx';
import { supabase } from '../lib/supabase';
import PdfViewerModal from '../components/PdfViewerModal.jsx';

const CATEGORY_COLORS = {
  History:      { bg: '#FEF3E2', text: '#8B6914' },
  Politics:     { bg: '#FDEDEC', text: '#C0392B' },
  Economics:    { bg: '#EBF5FB', text: '#2980B9' },
  Culture:      { bg: '#F5EEF8', text: '#8E44AD' },
  Science:      { bg: '#EAFAF1', text: '#27AE60' },
  Architecture: { bg: '#FEF9E7', text: '#D68910' },
  Lectures:     { bg: '#FFF0E8', text: '#E8672E' },
};

const CATEGORIES = ['All', 'History', 'Politics', 'Economics', 'Culture', 'Science', 'Architecture', 'Lectures'];

function CategoryPill({ label, active, onClick }) {
  const colors = CATEGORY_COLORS[label] || { bg: '#FFF0E8', text: '#E8672E' };
  return (
    <button onClick={onClick} style={{
      padding: '0.45rem 1.1rem', borderRadius: '50px', cursor: 'pointer',
      fontFamily: "'Inter', sans-serif", fontSize: '0.82rem', fontWeight: '700',
      background: active ? (label === 'All' ? '#E8672E' : colors.text) : 'white',
      color: active ? 'white' : (label === 'All' ? '#17140F' : colors.text),
      border: `1.5px solid ${active ? 'transparent' : (label === 'All' ? '#E8E4DC' : colors.text + '40')}`,
      transition: 'all 0.2s ease',
      letterSpacing: '0.02em',
    }}>
      {label}
    </button>
  );
}

function FeaturedPost({ post, onClick, onViewPdf }) {
  const [hovered, setHovered] = useState(false);
  const colors = CATEGORY_COLORS[post.category] || CATEGORY_COLORS.Lectures;

  return (
    <article
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="featured-post"
      style={{
        borderRadius: '20px', overflow: 'hidden', cursor: 'pointer',
        display: 'flex', flexDirection: 'row', minHeight: '360px',
        marginBottom: '3rem', background: 'white',
        boxShadow: hovered ? '0 20px 60px rgba(0,0,0,0.13)' : '0 4px 24px rgba(0,0,0,0.07)',
        border: '1px solid rgba(0,0,0,0.06)',
        transition: 'box-shadow 0.4s ease',
      }}
    >
      {/* Left: text */}
      <div style={{
        flex: 1, padding: '2.5rem', display: 'flex',
        flexDirection: 'column', justifyContent: 'center', minWidth: 0,
      }}>
        {/* Featured + category row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
          <span style={{
            background: '#E8672E', color: 'white',
            fontSize: '0.68rem', fontWeight: '800', letterSpacing: '0.1em',
            textTransform: 'uppercase', padding: '0.3rem 0.85rem', borderRadius: '50px',
          }}>Featured</span>
          <span style={{
            background: colors.bg, color: colors.text,
            fontSize: '0.68rem', fontWeight: '800', letterSpacing: '0.07em',
            textTransform: 'uppercase', padding: '0.3rem 0.85rem', borderRadius: '50px',
          }}>{post.category}</span>
          <span style={{ color: '#6B665C', fontSize: '0.82rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
            <Clock size={13} /> {post.reading_time} min read
          </span>
        </div>

        <h2 style={{
          fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Helvetica Neue', Arial, sans-serif",
          fontSize: 'clamp(1.35rem, 2.8vw, 2rem)', fontWeight: '600',
          color: '#17140F', margin: '0 0 0.85rem', lineHeight: 1.2,
          letterSpacing: '-0.02em',
        }}>
          {post.title}
        </h2>

        <p style={{
          color: '#6B665C', fontSize: '0.95rem', lineHeight: 1.7,
          margin: '0 0 1.75rem', flex: 1,
        }}>
          {post.meta_description || textSnippet(post.content)}
        </p>

        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          <button style={{
            display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
            background: '#E8672E', color: 'white', border: 'none',
            borderRadius: '10px', padding: '0.75rem 1.5rem', fontSize: '0.88rem', fontWeight: '700',
            cursor: 'pointer', fontFamily: "'Inter', sans-serif",
            boxShadow: '0 4px 20px rgba(232, 103, 46,0.35)',
          }}>
            Read More <ArrowRight size={15} />
          </button>
          {post.pdf_url && (
            <button
              onClick={e => { e.stopPropagation(); onViewPdf(post.pdf_url, post.title); }}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
                background: '#F7F5F0', color: '#17140F',
                border: '1.5px solid #E8E4DC',
                borderRadius: '10px', padding: '0.75rem 1.25rem', fontSize: '0.88rem', fontWeight: '600',
                cursor: 'pointer', fontFamily: "'Inter', sans-serif",
              }}
            >
              <BookOpen size={15} /> View Notes
            </button>
          )}
        </div>
      </div>

      {/* Right: image */}
      <div className="featured-img-wrap" style={{
        width: '420px', flexShrink: 0, position: 'relative', overflow: 'hidden',
        background: 'linear-gradient(135deg, #2A1F0E 0%, #17140F 100%)',
      }}>
        {post.poster_url && (
          <img
            loading="lazy"
            decoding="async"
            src={post.poster_url}
            alt={post.poster_alt || post.title}
            style={{
              width: '100%', height: '100%', objectFit: 'cover', display: 'block',
              transform: hovered ? 'scale(1.04)' : 'scale(1)',
              transition: 'transform 0.6s cubic-bezier(0.22,1,0.36,1)',
            }}
          />
        )}
      </div>
    </article>
  );
}

function PostCard({ post, index, onClick, onViewPdf }) {
  const [hovered, setHovered] = useState(false);
  const colors = CATEGORY_COLORS[post.category] || CATEGORY_COLORS.Lectures;

  return (
    <article
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: 'white', borderRadius: '16px', overflow: 'hidden', cursor: 'pointer',
        boxShadow: hovered ? '0 16px 48px rgba(0,0,0,0.12)' : '0 2px 12px rgba(0,0,0,0.05)',
        border: '1px solid rgba(0,0,0,0.05)',
        transform: hovered ? 'translateY(-4px)' : 'translateY(0)',
        transition: 'all 0.4s cubic-bezier(0.22,1,0.36,1)',
        animation: `fadeInUp 0.5s ease-out ${index * 0.08}s both`,
        display: 'flex', flexDirection: 'column',
      }}
    >
      {/* Thumbnail */}
      <div style={{ height: '220px', position: 'relative', overflow: 'hidden', flexShrink: 0 }}>
        {post.poster_url
          ? <img
              loading="lazy" decoding="async"
              src={post.poster_url} alt={post.poster_alt || post.title}
              style={{
                width: '100%', height: '100%', objectFit: 'cover',
                transform: hovered ? 'scale(1.06)' : 'scale(1)',
                transition: 'transform 0.6s cubic-bezier(0.22,1,0.36,1)',
              }}
            />
          : <div style={{
              width: '100%', height: '100%',
              background: 'linear-gradient(135deg, #2A1F0E 0%, #17140F 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '2.5rem',
            }}>🎙️</div>
        }
        {/* Category badge */}
        <div style={{
          position: 'absolute', top: '1rem', left: '1rem',
          background: colors.bg, color: colors.text,
          fontSize: '0.68rem', fontWeight: '800', letterSpacing: '0.07em',
          textTransform: 'uppercase', padding: '0.28rem 0.75rem', borderRadius: '50px',
        }}>
          {post.category}
        </div>
      </div>

      {/* Body */}
      <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.6rem' }}>
          <span style={{ fontSize: '0.78rem', color: '#E8672E', fontWeight: '700' }}>
            {post.published_at
              ? new Date(post.published_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
              : ''}
          </span>
          <span style={{ fontSize: '0.78rem', color: '#6B665C', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
            <Clock size={12} /> {post.reading_time} min read
          </span>
        </div>

        <h3 style={{
          fontSize: '1.05rem', fontWeight: '800', color: '#17140F',
          margin: '0 0 0.6rem', lineHeight: 1.35, letterSpacing: '-0.01em',
        }}>
          {post.title}
        </h3>

        <p style={{
          fontSize: '0.88rem', color: '#6B665C', lineHeight: 1.65,
          margin: '0 0 1.25rem', flex: 1,
          display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden',
        }}>
          {post.meta_description || textSnippet(post.content)}
        </p>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 'auto' }}>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
            color: '#E8672E', fontWeight: '700', fontSize: '0.85rem',
          }}>
            Read More <ChevronRight size={14} />
          </span>
          {post.pdf_url && (
            <button
              onClick={e => { e.stopPropagation(); onViewPdf(post.pdf_url, post.title); }}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
                color: '#6B665C', fontSize: '0.8rem', fontWeight: '600',
                padding: '0.35rem 0.75rem', border: 'none',
                background: '#F7F5F0', borderRadius: '6px',
                cursor: 'pointer', fontFamily: "'Inter', sans-serif",
              }}
              onMouseEnter={e => { e.currentTarget.style.background = '#E8672E'; e.currentTarget.style.color = 'white'; }}
              onMouseLeave={e => { e.currentTarget.style.background = '#F7F5F0'; e.currentTarget.style.color = '#6B665C'; }}
            >
              <Download size={13} /> Notes
            </button>
          )}
        </div>
      </div>
    </article>
  );
}

function textSnippet(html = '', words = 25) {
  return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim().split(' ').slice(0, words).join(' ') + '…';
}

export default function PreviousLectures() {
  const [posts,        setPosts]        = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [activeCategory, setActiveCategory] = useState('All');
  const [searchQuery,  setSearchQuery]  = useState('');
  const [visibleCount, setVisibleCount] = useState(6);
  const navigate = useNavigate();
  const [pdfModal, setPdfModal] = useState({ open: false, url: '', title: '' });
  const openPdf = (url, title) => setPdfModal({ open: true, url, title });

  useEffect(() => {
    supabase
      .from('posts')
      .select('id, title, slug, category, tags, pdf_url, poster_url, poster_alt, meta_description, reading_time, word_count, published_at')
      .eq('status', 'published')
      .order('published_at', { ascending: false })
      .then(({ data, error }) => { if (error) console.error('Supabase fetch error:', error); setPosts(data || []); setLoading(false); });
  }, []);

  const filtered = useMemo(() => {
    let list = posts;
    if (activeCategory !== 'All') list = list.filter(p => p.category === activeCategory);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(p =>
        p.title.toLowerCase().includes(q) ||
        (p.tags || []).some(t => t.toLowerCase().includes(q)) ||
        p.category.toLowerCase().includes(q)
      );
    }
    return list;
  }, [posts, activeCategory, searchQuery]);

  const showFeatured = !searchQuery && activeCategory === 'All' && filtered.length > 0;
  const featured     = showFeatured ? filtered[0] : null;
  const gridPosts    = showFeatured ? filtered.slice(1, 1 + visibleCount) : filtered.slice(0, visibleCount);
  const hasMore      = showFeatured ? filtered.length > visibleCount + 1 : filtered.length > visibleCount;

  const goToPost = (slug) => navigate(`/lectures/${slug}`);

  return (
    <div style={{ fontFamily: "'Inter', sans-serif", background: '#FFFFFF', minHeight: '100vh' }}>
      <Seo title="Previous Lectures" description="Browse past Drunken Lectures — talks and panels on history, politics, economics, culture, and more." path="/lectures" />
      <Navbar />

      {/* ── Page hero ── */}
      <section style={{
        paddingTop: '120px', paddingBottom: '3rem',
        background: 'linear-gradient(180deg, #17140F 0%, #2A1F0E 100%)',
        textAlign: 'center', padding: '120px 1.5rem 4rem',
      }}>
        <div style={{
          display: 'inline-block', background: 'rgba(232, 103, 46,0.2)',
          border: '1px solid rgba(232, 103, 46,0.4)', color: '#FFB08A',
          fontSize: '0.7rem', fontWeight: '700', letterSpacing: '0.12em',
          textTransform: 'uppercase', padding: '0.35rem 1rem', borderRadius: '50px',
          marginBottom: '1.25rem',
        }}>Past Lectures</div>
        <h1 style={{
          fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Helvetica Neue', Arial, sans-serif",
          fontSize: 'clamp(2.2rem, 5vw, 3.5rem)', fontWeight: '600', color: 'white',
          margin: '0 0 1rem', lineHeight: 1.1, letterSpacing: '-0.02em',
        }}>
          Every Talk.<br />
          <span className="accent-underline" style={{ color: '#F0A576' }}>Every Story.</span>
        </h1>
        <p style={{
          fontSize: '1rem', color: 'rgba(255,255,255,0.65)', maxWidth: '440px',
          margin: '0 auto', lineHeight: 1.7,
        }}>
          Browse all our past lectures — full read-throughs and downloadable notes available.
        </p>
      </section>

      {/* ── Filters + search ── */}
      <div style={{
        background: 'white', borderBottom: '1px solid #EDE6D6',
        position: 'sticky', top: '64px', zIndex: 90,
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '1rem 1.5rem', display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          {/* Search */}
          <div style={{ position: 'relative', flexShrink: 0 }}>
            <Search size={15} style={{ position: 'absolute', left: '0.85rem', top: '50%', transform: 'translateY(-50%)', color: '#6B665C' }} />
            <input
              value={searchQuery}
              onChange={e => { setSearchQuery(e.target.value); setVisibleCount(6); }}
              placeholder="Search lectures…"
              style={{
                paddingLeft: '2.25rem', paddingRight: '1rem', paddingTop: '0.55rem', paddingBottom: '0.55rem',
                border: '1.5px solid #E8E4DC', borderRadius: '50px', fontSize: '0.85rem',
                fontFamily: "'Inter', sans-serif", outline: 'none', width: 'min(200px, 100%)', color: '#17140F',
              }}
              onFocus={e => { e.target.style.borderColor = '#E8672E'; }}
              onBlur={e  => { e.target.style.borderColor = '#E8E4DC'; }}
            />
          </div>

          {/* Category pills */}
          <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', flex: 1 }}>
            {CATEGORIES.map(cat => (
              <CategoryPill
                key={cat} label={cat}
                active={activeCategory === cat}
                onClick={() => { setActiveCategory(cat); setVisibleCount(6); }}
              />
            ))}
          </div>
        </div>
      </div>

      {/* ── Main content ── */}
      <main style={{ maxWidth: '1200px', margin: '0 auto', padding: '3rem 1.5rem' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '6rem 0', color: '#6B665C' }}>
            <div style={{
              width: '40px', height: '40px', border: '3px solid #EDE6D6',
              borderTopColor: '#E8672E', borderRadius: '50%',
              animation: 'spin 0.8s linear infinite', margin: '0 auto 1rem',
            }} />
            Loading lectures…
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '6rem 0', color: '#6B665C' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>🔍</div>
            <p style={{ fontWeight: '600', margin: '0 0 0.5rem' }}>No lectures found</p>
            <p style={{ fontSize: '0.9rem' }}>Try a different search or category</p>
          </div>
        ) : (
          <>
            {/* Featured post */}
            {featured && (
              <FeaturedPost post={featured} onClick={() => goToPost(featured.slug)} onViewPdf={openPdf} />
            )}

            {/* Grid */}
            {gridPosts.length > 0 && (
              <>
                {showFeatured && featured && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.75rem' }}>
                    <div style={{ flex: 1, height: '1px', background: '#EDE6D6' }} />
                    <span style={{ fontSize: '0.75rem', fontWeight: '700', color: '#6B665C', letterSpacing: '0.08em', textTransform: 'uppercase' }}>More Lectures</span>
                    <div style={{ flex: 1, height: '1px', background: '#EDE6D6' }} />
                  </div>
                )}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                  gap: '1.75rem',
                }}>
                  {gridPosts.map((post, i) => (
                    <PostCard key={post.id} post={post} index={i} onClick={() => goToPost(post.slug)} onViewPdf={openPdf} />
                  ))}
                </div>
              </>
            )}

            {/* Load more */}
            {hasMore && (
              <div style={{ textAlign: 'center', marginTop: '3rem' }}>
                <button
                  onClick={() => setVisibleCount(c => c + 6)}
                  style={{
                    background: 'white', color: '#17140F',
                    border: '1.5px solid #E8E4DC', borderRadius: '50px',
                    padding: '0.85rem 2.5rem', fontSize: '0.9rem', fontWeight: '700',
                    cursor: 'pointer', fontFamily: "'Inter', sans-serif",
                    transition: 'all 0.2s ease',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = '#E8672E'; e.currentTarget.style.color = '#E8672E'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = '#E8E4DC'; e.currentTarget.style.color = '#17140F'; }}
                >
                  Load More Lectures
                </button>
              </div>
            )}
          </>
        )}
      </main>

      {pdfModal.open && (
        <PdfViewerModal
          url={pdfModal.url}
          title={pdfModal.title}
          onClose={() => setPdfModal({ open: false, url: '', title: '' })}
        />
      )}

      <Footer />

      <style>{`
        * { box-sizing: border-box; } body { margin: 0; padding: 0; }
        @keyframes fadeInUp { from { opacity: 0; transform: translateY(24px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes spin { to { transform: rotate(360deg); } }
        @media (min-width: 900px) { .desktop-nav { display: flex !important; } .mobile-menu-btn { display: none !important; } }
        @media (max-width: 899px) { .desktop-nav { display: none !important; } .mobile-menu-btn { display: flex !important; } }
        @media (max-width: 768px) {
          .featured-post { flex-direction: column !important; min-height: auto !important; }
          .featured-img-wrap { width: 100% !important; height: 240px !important; order: -1; }
        }
        a:focus-visible, button:focus-visible { outline: 2px solid #E8672E; outline-offset: 2px; }
      `}</style>
    </div>
  );
}
